import { ReactNode, useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import GuardianIcon from "@/components/common/GuardianIcon"
import ElementIconImage from "@/components/common/ElementIconImage"
import LeagueLabel from "@/components/guardians/LeagueLabel"
import { deletePassport, setAvatarVariant, updateNickname } from "@/api/gameApi"
import { getGameNotifications } from "@/api/gameNotificationsApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import {
  AFFILIATION_ELEMENT_TITLES,
  AFFILIATION_PASSIVE_PHASE_HINT,
  PVP_STATS_LEGACY_NOTE,
} from "@/constants/affiliationInfo"
import {
  DECK_BATTLE_ENABLED,
  ELEMENT_NAMES,
  USE_MOCK_BATTLE,
} from "@/constants/guardians"
import { formatOneOnOneRewardsLeftLabel, REFERRAL_REWARD_CHAKRA } from "@/constants/game.constants"
import { formatGuardianPathLabel } from "@/constants/guardianPaths"
import { LEAGUE_PRIVILEGES } from "@/constants/factionInfo"
import { REDEEM_UI } from "@/constants/redeemCopy"
import { useAuth } from "@/context/AuthContext"
import AvatarVariantPicker from "@/features/passport/components/AvatarVariantPicker"
import GuardianPassportCard, {
  passportCardFromDigitalPassport,
} from "@/features/passport/components/GuardianPassportCard"
import ProfileActionsModal from "@/features/passport/components/ProfileActionsModal"
import PetActiveTimeWidget from "@/features/pet/components/PetActiveTimeWidget"
import { ProfileMenuLink } from "@/features/profile/ProfileMenuLink"
import {
  getCachedGuardiansCities,
  resolveGuardiansCityLabel,
} from "@/features/passport/guardiansCity"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { canUseBattleFxLab } from "@/utils/guardiansAccess"
import { formatRuUntil } from "@/utils/geo"
import { needsAffiliationSelection } from "@/utils/onboardingPassport"
import {
  formatElementStatLine,
  formatWinRate,
} from "@/utils/pvpStatsFormat"
import { GuardianIconName } from "@/constants/guardianIconNames"
import { CardElement } from "@/types/battle"

const NICK_MIN = 2
const NICK_MAX = 100

export default function GuardiansProfileTab() {
  const navigate = useNavigate()
  const { profile, logout } = useAuth()
  const showBattleFxLab = canUseBattleFxLab(profile)
  const {
    passport,
    refreshPassport,
    applyPassport,
    clearPassport,
    loading,
  } = useGuardiansPassport()

  const [refreshing, setRefreshing] = useState(false)
  const [nicknameDraft, setNicknameDraft] = useState("")
  const [savingNick, setSavingNick] = useState(false)
  const [savingAvatar, setSavingAvatar] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [unreadNotifications, setUnreadNotifications] = useState(0)

  useEffect(() => {
    if (passport?.nickname) setNicknameDraft(passport.nickname)
  }, [passport?.nickname])

  const refreshUnread = useCallback(async () => {
    try {
      const { items } = await getGameNotifications()
      setUnreadNotifications(items.filter((n) => !n.isReaded).length)
    } catch {
      setUnreadNotifications(0)
    }
  }, [])

  useEffect(() => {
    void refreshUnread()
  }, [refreshUnread])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await Promise.all([refreshPassport(), refreshUnread()])
    } finally {
      setRefreshing(false)
    }
  }, [refreshPassport, refreshUnread])

  const saveNickname = async () => {
    const trimmed = nicknameDraft.trim()
    if (trimmed.length < NICK_MIN || trimmed.length > NICK_MAX) {
      window.alert(`От ${NICK_MIN} до ${NICK_MAX} символов`)
      return
    }
    if (passport && trimmed === passport.nickname) return
    if (passport?.nicknameRename && !passport.nicknameRename.canRename) {
      window.alert(
        `Менять имя можно раз в 6 месяцев. Осталось ~${passport.nicknameRename.daysLeft} дн.`
      )
      return
    }
    setSavingNick(true)
    try {
      if (USE_MOCK_BATTLE) {
        applyPassport({ ...passport!, nickname: trimmed })
        return
      }
      const updated = await updateNickname(trimmed)
      applyPassport(updated)
      window.alert("Никнейм обновлён. Следующая смена — через 6 месяцев.")
    } catch (e) {
      window.alert(getGameApiErrorMessage(e, "Не удалось сохранить никнейм"))
    } finally {
      setSavingNick(false)
    }
  }

  const saveAvatar = async (variant: 1 | 2 | 3) => {
    if (!passport) return
    if ((passport.avatarVariant ?? 1) === variant) return
    setSavingAvatar(true)
    try {
      if (USE_MOCK_BATTLE) {
        applyPassport({ ...passport, avatarVariant: variant })
        return
      }
      const updated = await setAvatarVariant(variant)
      applyPassport(updated)
    } catch (e) {
      window.alert(getGameApiErrorMessage(e, "Не удалось сменить аватар"))
    } finally {
      setSavingAvatar(false)
    }
  }

  const confirmRestart = () => {
    if (
      !window.confirm(
        "Начать заново? Весь прогресс будет потерян. Никнейм, лига и код сохранятся. Далее вы выберете 2 новые стартовые карты."
      )
    ) {
      return
    }
    navigate("/play/onboarding/starter")
  }

  const runDelete = async () => {
    setDeleting(true)
    try {
      if (!USE_MOCK_BATTLE) await deletePassport()
      clearPassport()
      navigate("/play/onboarding", { replace: true })
    } catch (e) {
      window.alert(getGameApiErrorMessage(e, "Не удалось удалить профиль"))
    } finally {
      setDeleting(false)
    }
  }

  const confirmDelete = () => {
    if (
      !window.confirm(
        "Удалить игровой профиль? Паспорт и все карты будут удалены. Аккаунт daksby останется."
      )
    ) {
      return
    }
    if (
      !window.confirm(
        "Это действие нельзя отменить. Создать профиль можно будет заново."
      )
    ) {
      return
    }
    void runDelete()
  }

  const confirmExit = async () => {
    if (!window.confirm("Выйти из игры? Вы вернётесь на авторизацию.")) return
    await logout()
    navigate("/login", { replace: true })
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-guardians-accent border-t-transparent" />
      </div>
    )
  }

  if (!passport) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6">
        <p className="text-lg font-extrabold text-white">Нет игрового профиля</p>
        <Link
          to="/play/onboarding"
          className="rounded-xl bg-guardians-accent px-6 py-3 text-sm font-extrabold text-guardians-bg"
        >
          Начать игру
        </Link>
      </div>
    )
  }

  const p = passport
  const cityLabel = resolveGuardiansCityLabel(
    p,
    null,
    profile as { city?: { title?: string } | string | null; city_id?: number },
    getCachedGuardiansCities()
  )
  const nickChanged = nicknameDraft.trim() !== p.nickname
  const pvpStats = p.pvpStats
  const elementHighlights = p.elementHighlights
  const minBattlesForBest = elementHighlights?.bestElementMinBattles ?? 5

  return (
    <div className="px-5 py-5 pb-8">
      <button
        type="button"
        disabled={refreshing}
        onClick={() => void onRefresh()}
        className="mb-3 w-full rounded-lg border border-white/10 py-1.5 text-xs text-white/50"
      >
        {refreshing ? "Обновление…" : "Обновить профиль"}
      </button>

      <div className="flex flex-col gap-2.5">
        <GuardianPassportCard
          profile={passportCardFromDigitalPassport(p, cityLabel)}
          showRankBadge
          applyLocalCardSkin
        />

        <div className="flex flex-row items-center gap-2">
          <input
            value={nicknameDraft}
            onChange={(e) => setNicknameDraft(e.target.value)}
            maxLength={NICK_MAX}
            placeholder="Игровое имя"
            className="flex-1 rounded-[14px] border border-white/10 bg-white/[0.08] px-3.5 py-3 text-[15px] text-white outline-none focus:border-guardians-accent/40"
          />
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border border-white/10 bg-white/[0.08] text-xl text-white/85"
            onClick={() => setActionsOpen(true)}
            aria-label="Действия с профилем"
          >
            ⋯
          </button>
        </div>

        {p.nicknameRename && !p.nicknameRename.canRename ? (
          <p className="-mt-1 text-xs text-white/40">
            Смена имени через ~{p.nicknameRename.daysLeft} дн. (раз в 6 месяцев)
          </p>
        ) : (
          <p className="-mt-1 text-xs text-white/40">Имя можно менять раз в 6 месяцев</p>
        )}

        {nickChanged ? (
          <button
            type="button"
            disabled={
              savingNick || Boolean(p.nicknameRename && !p.nicknameRename.canRename)
            }
            onClick={() => void saveNickname()}
            className="w-full rounded-xl bg-guardians-accent py-2.5 text-sm font-extrabold text-guardians-bg disabled:opacity-50"
          >
            {savingNick ? "…" : "Сохранить никнейм"}
          </button>
        ) : null}

        <AvatarVariantPicker
          element={p.affiliationElement}
          selected={p.avatarVariant}
          onSelect={(v) => void saveAvatar(v)}
          disabled={savingAvatar}
          saving={savingAvatar}
        />

        <PetActiveTimeWidget passport={p} />

        <ProfileMenuLink
          to="/play/airship"
          icon="airship"
          title="Дирижабль"
          sub="Запуск разведки и добыча из трюма"
        />
        <ProfileMenuLink
          to="/play/notifications"
          icon="notice"
          title="Уведомления"
          sub={
            unreadNotifications > 0
              ? `Непрочитанных: ${unreadNotifications}`
              : "Сообщения от Хранителей"
          }
          badge={unreadNotifications > 0}
        />
        <ProfileMenuLink
          to="/play/city-chronicle"
          icon="chronicle"
          title="Хроника города"
          sub="Битва, карты, герои и итоги дня"
        />
        <ProfileMenuLink
          to="/play/almanac"
          icon="almanac"
          title="Великий Альманах Стихий"
          sub="Журнал once-owned: стихия × стиль × ранг"
        />
        <ProfileMenuLink
          to="/play/battle-history"
          icon="history"
          title="История боёв"
          sub="PvP-результаты и разбор силы"
        />
        <ProfileMenuLink
          to="/play/redeem"
          iconNode={<span className="text-xl text-guardians-accent">⌁</span>}
          title={REDEEM_UI.profileTitle}
          sub={REDEEM_UI.profileSubtitle}
        />
        <ProfileMenuLink
          to="/play/referral"
          icon="friends"
          title="Пригласи друга"
          sub={`+${REFERRAL_REWARD_CHAKRA} чакры за каждого, кто пройдёт обучение`}
        />
        <ProfileMenuLink
          to="/play/wardrobe"
          icon="wardrobe"
          title="Примерочная"
          sub="Примерь любую карту: ранг, стихия, стиль, осколки"
        />

        {showBattleFxLab ? (
          <>
            <ProfileMenuLink
              to="/play/help"
              title="Лаборатория FX"
              sub="Тест анимаций стихий и стилей"
              iconNode={<span className="text-xl text-cyan-400">✦</span>}
            />
          </>
        ) : null}

        {needsAffiliationSelection(p) ? (
          <Link
            to="/play/onboarding/affiliation"
            className="block rounded-[14px] border border-guardians-accent/35 bg-guardians-accent/10 p-3.5"
          >
            <p className="text-[15px] font-extrabold text-guardians-accent">
              Выберите стихию хранителя
            </p>
            <p className="text-xs text-white/55">Нужно для PvP-боев (радар, QR, вызовы)</p>
          </Link>
        ) : null}

        {p.affiliationElement && p.affiliationPassive?.description ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-bold text-white">Пассив хранителя</h2>
            <div className="rounded-[14px] bg-white/[0.05] p-3.5">
              <p className="text-xs leading-relaxed text-white/65">
                {p.affiliationPassive.description}
              </p>
              <p className="mt-2 text-[11px] leading-snug text-white/40">
                {AFFILIATION_PASSIVE_PHASE_HINT}
              </p>
            </div>
          </section>
        ) : null}

        <StatsSection title="PvP статистика">
          <InfoRow label="Победы" value={String(pvpStats?.pvpWins ?? 0)} />
          <InfoRow label="Поражения" value={String(pvpStats?.pvpLosses ?? 0)} />
          <InfoRow label="Ничьи" value={String(pvpStats?.pvpDraws ?? 0)} />
          <InfoRow label="Winrate" value={formatWinRate(pvpStats?.winRate)} />
          <p className="mt-1 text-[11px] text-white/35">{PVP_STATS_LEGACY_NOTE}</p>
        </StatsSection>

        {DECK_BATTLE_ENABLED ? (
          <StatsSection title="Дуэль колодой">
            <InfoRow label="Победы" value={String(p.deckPvpStats?.pvpWins ?? 0)} />
            <InfoRow label="Поражения" value={String(p.deckPvpStats?.pvpLosses ?? 0)} />
            <InfoRow label="Ничьи" value={String(p.deckPvpStats?.pvpDraws ?? 0)} />
            <InfoRow label="Winrate" value={formatWinRate(p.deckPvpStats?.winRate)} />
          </StatsSection>
        ) : null}

        {p.elementStats?.length ? (
          <StatsSection title="Статистика по стихиям карт">
            {p.elementStats.map((stat) => (
              <InfoRow
                key={stat.element}
                element={stat.element}
                label={ELEMENT_NAMES[stat.element]}
                value={formatElementStatLine(stat.wins, stat.losses, stat.winRate)}
                compactValue
              />
            ))}
            {elementHighlights?.favoriteElement ? (
              <HighlightLine element={elementHighlights.favoriteElement}>
                Любимая стихия в боях:{" "}
                {AFFILIATION_ELEMENT_TITLES[elementHighlights.favoriteElement]}
              </HighlightLine>
            ) : null}
            {elementHighlights?.bestElement ? (
              <HighlightLine element={elementHighlights.bestElement}>
                Лучший winrate (≥{minBattlesForBest} боёв):{" "}
                {AFFILIATION_ELEMENT_TITLES[elementHighlights.bestElement]}
              </HighlightLine>
            ) : null}
          </StatsSection>
        ) : null}

        <div className="rounded-2xl bg-white/[0.05] p-4">
          <InfoRow
            label="Лига"
            value={<LeagueLabel league={p.academicLeague} iconSize={22} />}
          />
          <InfoRow label="Путь" value={formatGuardianPathLabel(p.guardianPath)} />
          {p.guardianPathInfo?.description ? (
            <InfoRow label="Специализация" value={p.guardianPathInfo.description} />
          ) : null}
          {p.academicLeague ? (
            <>
              <InfoRow label="PvE бонус" value={LEAGUE_PRIVILEGES[p.academicLeague].pve} />
              <InfoRow label="Карта" value={LEAGUE_PRIVILEGES[p.academicLeague].map} />
              <InfoRow label="PvP бонус" value={LEAGUE_PRIVILEGES[p.academicLeague].pvp} />
            </>
          ) : null}
          {p.minorityBonusUntil ? (
            <InfoRow label="Бонус меньшинства" value={formatRuUntil(p.minorityBonusUntil)} />
          ) : null}
          <InfoRow label="Город" value={cityLabel} />
          <InfoRow label="Энергия" icon="energy" value={`${p.energy}/${p.maxEnergy}`} />
          <InfoRow label="Монеты" icon="monet" value={String(p.coins)} />
          <InfoRow label="Чакра" icon="chakra" value={String(p.chakraCharges)} />
          <InfoRow label="Глоб. очки" value={String(p.globalPts)} />
          <InfoRow
            label={cityLabel !== "—" ? `Лок. очки (${cityLabel})` : "Лок. очки"}
            value={String(p.localPts)}
          />
          <InfoRow label="Усилители слияния" value={String(p.fuseBoosters ?? 0)} />
          <InfoRow label="Токены смены стиля" value={String(p.styleChangeTokens ?? 0)} />
          <InfoRow
            label="Бои 1 на 1 сегодня"
            value={`${p.qrBattlesToday} · ${formatOneOnOneRewardsLeftLabel(p.qrBattlesToday).toLowerCase()}`}
          />
        </div>
      </div>

      <ProfileActionsModal
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        onSupport={() => {
          window.location.href = "mailto:support@daks.by"
        }}
        onRestart={confirmRestart}
        onExit={() => void confirmExit()}
        onDelete={confirmDelete}
        deleting={deleting}
      />
    </div>
  )
}

function StatsSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-bold text-white">{title}</h2>
      <div className="flex flex-col gap-2.5 rounded-2xl bg-white/[0.05] p-4">{children}</div>
    </section>
  )
}

function InfoRow({
  label,
  value,
  compactValue,
  element,
  icon,
}: {
  label: string
  value: ReactNode
  compactValue?: boolean
  element?: CardElement
  icon?: GuardianIconName
}) {
  return (
    <div className="flex flex-row items-center justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-row items-center gap-1.5">
        {element ? <ElementIconImage element={element} size={16} /> : null}
        {icon ? <GuardianIcon name={icon} size={22} /> : null}
        <span className="text-[13px] text-white/50">{label}</span>
      </div>
      {typeof value === "string" ? (
        <span
          className={`flex-1 text-right font-bold text-white ${
            compactValue ? "text-[11px]" : "text-[13px]"
          }`}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  )
}

function HighlightLine({
  element,
  children,
}: {
  element: CardElement
  children: ReactNode
}) {
  return (
    <div className="mt-2 flex flex-row items-center gap-2">
      <ElementIconImage element={element} size={16} />
      <p className="flex-1 text-xs text-white/55">{children}</p>
    </div>
  )
}
